import * as React from 'react';

import { useIntl } from 'react-intl';

import { InternalStoreDiagnosticModal } from 'components/store-diagnostic-modal';
import { IRestaurantNode } from 'generated/rbi-graphql';
import { useFavoriteStore } from 'hooks/favorite-stores';
import { useRestaurantGroups } from 'hooks/use-restaurant-groups';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { LOYALTY_EXCLUDED_GROUP } from './constants';
import { useDiagnostics } from './hooks/use-diagnostics';
import { useDistanceText } from './hooks/use-distance-text';
import { useErrorMessage } from './hooks/use-error-message';
import { useOpenClosedText } from './hooks/use-open-closed-text';
import { useServiceModeText } from './hooks/use-service-mode-text';
import { StoreCardView as NewStoreCardView } from './improved-store-card.view';
import { Wrapper } from './store-card.styled';
import { StoreCardView } from './store-card.view';
import { NewStoreCardViewProps, StoreCardViewProps } from './types';

interface StoreCardProps {
  restaurant: IRestaurantNode;
  onCardClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * StoreCard container component. This component is intended to be used on the
 * store locator. Data orchestration should happen at this level while keeping
 * the view layer more presentational.
 */
export const StoreCard: React.FC<StoreCardProps> = ({ restaurant, onCardClick }) => {
  const distanceText = useDistanceText({ restaurant });
  const errorMessage = useErrorMessage({ restaurant });
  const serviceModeText = useServiceModeText({ restaurant });
  const { formatMessage } = useIntl();
  const { storeHasMobileLoyalty } = useRestaurantGroups(LOYALTY_EXCLUDED_GROUP);

  const enableImprovedStoreSelection = useFlag(
    LaunchDarklyFlag.ENABLE_IMPROVED_STORE_SELECTION_OPTIONS
  );

  const {
    data: diagnosticsData,
    showModal: showDiagnosticsModal,
    onClick: onDiagnosticsClick,
    onDismiss: showDiagnosticsModalDismiss,
  } = useDiagnostics({ restaurant });

  const {
    ErrorFavoriteStoreDialog,
    favStoreLoading,
    isFavorite,
    toggleFavorite: onFavoriteClick,
  } = useFavoriteStore(restaurant._id ?? '', restaurant.number ?? '');

  const { isOpen, text, operatingHours } = useOpenClosedText(restaurant, true);

  const Card = React.createElement<StoreCardViewProps | NewStoreCardViewProps>(
    enableImprovedStoreSelection ? NewStoreCardView : StoreCardView,
    {
      restaurant,
      isFavorite,
      onFavoriteClick,
      onDiagnosticsClick,
      isUpdatingFavorites: favStoreLoading,
      onCardClick: enableImprovedStoreSelection ? undefined : onCardClick,
      storeDetails: {
        body: serviceModeText,
        distanceText,
        errorMessage,
        ...(isOpen ? { openText: text } : { closedText: text }),
        title: restaurant.customerFacingAddress?.locale || restaurant.physicalAddress?.address1,
        isHalal: Boolean(restaurant.isHalal),
        inStoreLoyalty: Boolean(restaurant.hasLoyalty),
        mobileLoyalty: storeHasMobileLoyalty(restaurant.storeId || ''),
        operatingHours,
      },
    }
  );

  return (
    <Wrapper data-testid={'store-card'}>
      {Card}

      {showDiagnosticsModal && (
        <InternalStoreDiagnosticModal
          onModalDismiss={showDiagnosticsModalDismiss}
          storeDiagnosticData={diagnosticsData}
        />
      )}
      <ErrorFavoriteStoreDialog buttonLabel={formatMessage({ id: 'signUp' })} />
    </Wrapper>
  );
};
